import { api, ApiError } from "../api.ts";
import { h, setMsg } from "../dom.ts";
import { navigate } from "../router.ts";
import {
  codeLoginEnabled,
  setUser,
  membershipOpen,
} from "../session.ts";
import { brandLockup, BRAND_NAME } from "../ui/brand.ts";
import { languageToggle } from "../i18n/lang.ts";
import { t } from "../i18n/index.ts";
import { joinCard } from "./join.ts";
import { takeReturn } from "../returnContext.ts";
import { emailSignIn } from "./emailSignIn.ts";

export function loginScreen(): HTMLElement {
  const el = h(`
    <main class="card auth-card">
      <div class="auth-top"><span class="lang-slot"></span></div>
      <h1 class="brand" aria-label="${BRAND_NAME}">${brandLockup}</h1>
      <p class="tagline">${t("login.tagline")}</p>
      <form id="password-login" novalidate>
        <label class="field"><span>${t("login.loginLabel")}</span>
          <input name="login" autocomplete="username" autofocus required /></label>
        <label class="field"><span>${t("login.password")}</span>
          <input name="password" type="password" autocomplete="current-password" required /></label>
        <button class="btn primary" type="submit">${t("login.signIn")}</button>
        <p class="msg" role="status" aria-live="polite"></p>
      </form>
      <p class="fine"><a data-link href="/forgot">${t("login.forgot")}</a></p>
      <button class="btn ghost code-toggle" type="button" hidden>${t("login.useCode")}</button>
      <div class="code-login" hidden>
        <form class="code-request" novalidate>
          <label class="field"><span>${t("login.loginLabel")}</span>
            <input name="identifier" autocomplete="username" required /></label>
          <label class="field"><span>${t("login.codeProvider")}</span>
            <select name="provider">
              <option value="email">${t("settings.providerEmail")}</option>
              <option value="telegram">Telegram</option>
            </select>
          </label>
          <button class="btn primary" type="submit">${t("login.sendCode")}</button>
          <p class="msg" role="status" aria-live="polite"></p>
        </form>
        <form class="code-verify" novalidate hidden>
          <label class="field"><span>${t("login.codeLabel")}</span>
            <input name="code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" required /></label>
          <button class="btn primary" type="submit">${t("login.verifyCode")}</button>
          <p class="msg" role="status" aria-live="polite"></p>
        </form>
      </div>
      <div class="join-cta" hidden>
        <p class="fine"><strong>${t("login.inviteOnlyLead")}</strong> ${t("login.inviteOnly")}</p>
        <button class="btn ghost brand-btn become-member" type="button">${t("login.becomeMember")}</button>
      </div>
    </main>
  `);

  el.querySelector(".lang-slot")!.append(languageToggle());
  void api.getConfig().then(config => {
    if (config.email_signin_enabled && el.isConnected) {
      el.querySelector("#password-login")!.before(emailSignIn(config.registration_mode === "public"));
    }
  }).catch(() => {});

  const form = el.querySelector<HTMLFormElement>("#password-login")!;
  const msg = el.querySelector(".msg") as HTMLElement;
  const button = form.querySelector("button.primary") as HTMLButtonElement;

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const data = new FormData(form);
    const login = String(data.get("login") ?? "").trim();
    const password = String(data.get("password") ?? "");
    if (!login || !password) {
      setMsg(msg, t("login.needBoth"), "bad");
      return;
    }
    button.disabled = true;
    setMsg(msg, t("login.signingIn"));
    try {
      const user = await api.login(login, password);
      setUser(user);
      navigate(takeReturn());
    } catch (err) {
      setMsg(msg, err instanceof ApiError ? err.message : t("login.couldNot"), "bad");
      button.disabled = false;
    }
  });

  void codeLoginEnabled().then((enabled) => {
    if (!enabled) return;
    const toggle = el.querySelector<HTMLButtonElement>(".code-toggle")!;
    const panel = el.querySelector<HTMLElement>(".code-login")!;
    const requestForm =
      panel.querySelector<HTMLFormElement>(".code-request")!;
    const verifyForm =
      panel.querySelector<HTMLFormElement>(".code-verify")!;
    toggle.hidden = false;
    toggle.onclick = () => {
      panel.hidden = !panel.hidden;
      toggle.textContent = panel.hidden
        ? t("login.useCode")
        : t("login.usePassword");
    };

    let requestId = "";
    requestForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      const data = new FormData(requestForm);
      const identifier = String(data.get("identifier") ?? "").trim();
      const provider = String(data.get("provider") ?? "email") as
        | "email"
        | "telegram";
      const requestMsg =
        requestForm.querySelector<HTMLElement>(".msg")!;
      const requestButton =
        requestForm.querySelector<HTMLButtonElement>("button")!;
      if (!identifier) {
        setMsg(requestMsg, t("forgot.need"), "bad");
        return;
      }
      requestButton.disabled = true;
      setMsg(requestMsg, t("login.sendingCode"));
      try {
        const issued = await api.requestLoginCode(identifier, provider);
        requestId = issued.request_id;
        requestForm.hidden = true;
        verifyForm.hidden = false;
        verifyForm.querySelector<HTMLInputElement>("input")!.focus();
      } catch (error) {
        setMsg(
          requestMsg,
          error instanceof ApiError ? error.message : t("login.codeFailed"),
          "bad",
        );
        requestButton.disabled = false;
      }
    });

    verifyForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      const code = String(
        new FormData(verifyForm).get("code") ?? "",
      ).trim();
      const verifyMsg =
        verifyForm.querySelector<HTMLElement>(".msg")!;
      const verifyButton =
        verifyForm.querySelector<HTMLButtonElement>("button")!;
      if (!/^\d{6}$/.test(code)) {
        setMsg(verifyMsg, t("login.codeNeed"), "bad");
        return;
      }
      verifyButton.disabled = true;
      setMsg(verifyMsg, t("login.signingIn"));
      try {
        const user = await api.verifyLoginCode(requestId, code);
        setUser(user);
        navigate(takeReturn());
      } catch (error) {
        setMsg(
          verifyMsg,
          error instanceof ApiError ? error.message : t("login.codeFailed"),
          "bad",
        );
        verifyButton.disabled = false;
      }
    });
  });

  // Reveal the apply CTA only when membership is open; wire it to swap the card
  // for the application form.
  void membershipOpen().then((open) => {
    if (!open) return;
    const cta = el.querySelector<HTMLElement>(".join-cta")!;
    cta.hidden = false;
    cta.querySelector(".become-member")!.addEventListener("click", () => {
      el.replaceWith(joinCard());
    });
  });

  return el;
}
